import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useNotifications, timeAgo } from '../context/NotificationContext'
import { useSSE } from '../hooks/useSSE'
import { useMarketData } from '../context/MarketDataContext'
import { SystemStatus } from './SystemStatus'
import type { PageView } from '../types'

interface LayoutProps {
  view: PageView
  onNavigate: (view: PageView) => void
  children: React.ReactNode
}

const adminNav: { label: string; view: PageView; icon: string }[] = [
  { label: 'Dashboard', view: 'admin-dashboard', icon: '◈' },
  { label: 'LiveWatch', view: 'trader-livewatch', icon: '⏱' },
  { label: 'Users', view: 'admin-users', icon: '⚇' },
  { label: 'Brokers', view: 'admin-brokers', icon: '⚯' },
  { label: 'Strategies', view: 'admin-strategies', icon: '⚙' },
  { label: 'Executions', view: 'admin-executions', icon: '▶' },
  { label: 'Reports', view: 'admin-reports', icon: '◷' },
  { label: 'Knowledge', view: 'knowledge', icon: '📖' },
  { label: 'Settings', view: 'admin-settings', icon: '⚙︎' },
]

const traderNav: { label: string; view: PageView; icon: string }[] = [
  { label: 'Dashboard', view: 'trader-dashboard', icon: '◈' },
  { label: 'LiveWatch', view: 'trader-livewatch', icon: '⏱' },
  { label: 'Strategies', view: 'trader-strategies', icon: '⚙' },
  { label: 'Orders & Trades', view: 'trader-orderbook', icon: '📋' },
  { label: 'Reports', view: 'trader-reports', icon: '◷' },
  { label: 'Archived', view: 'trader-archived', icon: '📁' },
]

const notifIcon: Record<string, string> = {
  trade: '📈', alert: '⚠️', system: '⚙️', warning: '⚡',
}

export function Layout({ view, onNavigate, children }: LayoutProps) {
  const { session, logout, isAdmin } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications()
  const sse = useSSE()
  const { ticks: marketTicks } = useMarketData()
  const nav = isAdmin ? adminNav : traderNav
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [currentTime, setCurrentTime] = useState('')
  const [showNotifs, setShowNotifs] = useState(false)

  // Build live market ticker from WebSocket/DO data
  const TICKER_TOKEN_MAP: Record<string, string> = {
    NIFTY: '101999957', BANKNIFTY: '101999972', SENSEX: '100000034',
    BANKEX: '100000035', INDIAVIX: '999920005',
  }
  const indicesToShow = ['NIFTY', 'BANKNIFTY', 'SENSEX', 'BANKEX', 'INDIAVIX']
  const marketTicker = indicesToShow.map(sym => {
    const token = TICKER_TOKEN_MAP[sym]
    const d: any = token ? marketTicks[token] : null
    if (!d || !d.ltp) return { label: sym === 'INDIAVIX' ? 'INDIA VIX' : sym, price: '—', chg: '—', up: true }
    const pChg = d.pChange ?? d.changePct ?? d.changePercent ?? 0
    const ltp = d.ltp ?? 0
    return {
      label: sym === 'INDIAVIX' ? 'INDIA VIX' : sym,
      price: Number(ltp).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      chg: pChg ? `${pChg > 0 ? '+' : ''}${Number(pChg).toFixed(2)}%` : '—',
      up: Number(pChg) >= 0,
    }
  })


  useEffect(() => {
    const tick = () => {
      const now = new Date()
      setCurrentTime(now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) + ' IST')
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  const goHome = () => {
    onNavigate(isAdmin ? 'admin-dashboard' : 'trader-dashboard')
    setSidebarOpen(false)
  }



  return (
    <>
      <div className="app-layout">

        {/* ── Mobile Top Bar ── */}
        <div className="mobile-top-bar">

          <div className="mobile-top-bar-left">
            <button className="mobile-hamburger" onClick={() => setSidebarOpen(o => !o)}>☰</button>
            <span className="sidebar-brand-pill" onClick={goHome} style={{ cursor: 'pointer' }}>
              <img src="/logo.png" alt="A" style={{ width: 24, height: 24, objectFit: 'contain' }} />
              <span className="pill-text">Aalap</span>
            </span>
          </div>
        </div>

        {/* ── Mobile Overlay ── */}
        {sidebarOpen && <div className="mobile-backdrop" onClick={() => setSidebarOpen(false)} />}


        {/* ── Sidebar ── */}
        <aside className={`sidebar ${sidebarOpen ? 'sidebar-mobile-open' : ''}`}>

        {/* Brand Pill */}
        <div className="sidebar-brand" onClick={goHome} style={{ cursor: 'pointer' }}>
          <span className="sidebar-brand-pill">
            <img src="/logo.png" alt="A" className="sidebar-logo-img" />
            <span className="pill-text">Aalap</span>
          </span>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {nav.map((item) => (
            <button
              key={item.view}
              className={`sidebar-link ${view === item.view ? 'active' : ''}`}
              onClick={() => { onNavigate(item.view); setSidebarOpen(false) }}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        {/* ── Sidebar Footer ── */}
        <div className="sidebar-footer">
          {/* System Status — real data from SystemStatus component */}
          <SystemStatus />

          {/* Theme + User Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0' }}>
            <div className="sidebar-theme-toggle" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`} style={{ margin: 0, flex: 'none' }}>
              <span>{theme === 'dark' ? '☀️' : '🌙'}</span>
              <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
            </div>
          </div>

          {/* User + Logout */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', borderTop: '1px solid var(--border)', paddingTop: '0.625rem' }}>
            <div className="sidebar-avatar">
              {(session?.user?.displayName || session?.user?.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="sidebar-user-info" style={{ flex: 1, minWidth: 0 }}>
              <div className="sidebar-user-name">{session?.user?.displayName || session?.user?.username}</div>
              <div className="sidebar-user-role">{session?.user?.role}</div>
            </div>
            <button className="sidebar-logout" onClick={logout} style={{ width: 'auto', padding: '0.375rem 0.75rem', flexShrink: 0 }}>Logout →</button>
          </div>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="main-content">

        {/* Market Ticker */}
        <div className="market-ticker">
          <span className={`live-dot ${sse.connected ? '' : 'disconnected'}`} />
          <span style={{ color: sse.connected ? 'var(--green)' : 'var(--red)', fontWeight: 800, fontSize: '0.625rem', letterSpacing: '0.05em' }}>
            {sse.connected ? 'LIVE' : 'OFFLINE'}
          </span>
          {marketTicker.map((m) => (
            <span className="ticker-item" key={m.label}>
              <span className="ticker-label">{m.label}</span>
              <span className="ticker-val">{m.price}</span>
              <span className={`ticker-chg ${m.up ? 'up' : 'down'}`}>{m.chg}</span>
            </span>
          ))}
          {/* Notification Bell */}
          <div style={{ position: 'relative', marginLeft: 'auto' }}>
            <button className="notif-bell" onClick={() => setShowNotifs(!showNotifs)} title="Notifications">
              🔔
              {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
            </button>

            {showNotifs && (
              <>
                <div className="notif-overlay" onClick={() => setShowNotifs(false)} />
                <div className="notif-panel">
                  <div className="notif-panel-header">
                    <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>Notifications</span>
                    {unreadCount > 0 && (
                      <button className="notif-mark-read" onClick={markAllRead}>Mark all read</button>
                    )}
                  </div>
                  <div className="notif-list">
                    {notifications.length === 0 ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No notifications</div>
                    ) : (
                      notifications.map(n => (
                        <div key={n.id} className={`notif-item ${n.read ? '' : 'unread'}`} onClick={() => markRead(n.id)}>
                          <span className="notif-icon">{notifIcon[n.type] || '⚙️'}</span>
                          <div className="notif-content">
                            <div className="notif-title">{n.title}</div>
                            <div className="notif-msg">{n.message}</div>
                            <div className="notif-time">{timeAgo(n.time)}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          <span className="ticker-time">{currentTime}</span>
        </div>

        {/* Page Content */}
        <div className="page-content-area">
          {children}
        </div>
      </main>

      {/* ── Mobile Bottom Tab Bar ── */}
      <nav className="mobile-bottom-tabs">
        {nav.slice(0, 5).map((item) => (
          <button
            key={item.view}
            className={`mobile-tab-btn ${view === item.view ? 'active' : ''}`}
            onClick={() => { onNavigate(item.view); setSidebarOpen(false) }}
          >
            <span className="mobile-tab-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Toasts are handled by ToastContext */}
      </div>

      {/* Fixed bottom notification bar for system errors */}
      {sse.systemError && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 'var(--sidebar-w, 240px)',
          right: 0,
          background: sse.systemError.startsWith('⚡') ? 'rgba(234, 179, 8, 0.95)' : 'rgba(239, 68, 68, 0.95)',
          color: '#fff',
          padding: '0.5rem 1rem',
          textAlign: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem',
          backdropFilter: 'blur(8px)',
          borderTop: '1px solid rgba(255,255,255,0.2)',
        }}>
          <span>{sse.systemError.startsWith('⚡') ? '⚡' : '⚠'}</span>
          <span>{sse.systemError.replace(/^⚡\s*/, '')}</span>
        </div>
      )}
    </>
  )
}
